import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../utilitarios/cn'

export type TomAviso = 'sucesso' | 'erro' | 'info' | 'atencao'

export interface Aviso {
  id: string
  tom: TomAviso
  titulo: string
  descricao?: string
}

interface ContextoAvisos {
  mostrar: (aviso: Omit<Aviso, 'id'>) => void
  sucesso: (titulo: string, descricao?: string) => void
  erro: (titulo: string, descricao?: string) => void
}

const Contexto = createContext<ContextoAvisos | null>(null)

const TONS: Record<TomAviso, string> = {
  sucesso: 'border-l-sucesso',
  erro: 'border-l-perigo',
  info: 'border-l-info',
  atencao: 'border-l-atencao',
}

const DURACAO_MS = 5500

/**
 * Avisos temporários (toast) — confirmação de interação registrada, de
 * transferência aplicada, de erro de rede.
 *
 * Não substitui `Alerta`: erro que exige correção de campo fica **no formulário**,
 * porque um toast desaparece antes de o usuário achar o campo errado. Toast é para
 * o que já aconteceu.
 */
export function ProvedorAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([])
  const sequencia = useRef(0)

  const remover = useCallback((id: string) => {
    setAvisos((atuais) => atuais.filter((aviso) => aviso.id !== id))
  }, [])

  const mostrar = useCallback(
    (aviso: Omit<Aviso, 'id'>) => {
      sequencia.current += 1
      const id = `aviso-${sequencia.current}`
      setAvisos((atuais) => [...atuais.slice(-3), { ...aviso, id }])
      window.setTimeout(() => remover(id), DURACAO_MS)
    },
    [remover],
  )

  const valor = useMemo<ContextoAvisos>(
    () => ({
      mostrar,
      sucesso: (titulo, descricao) => mostrar({ tom: 'sucesso', titulo, descricao }),
      erro: (titulo, descricao) => mostrar({ tom: 'erro', titulo, descricao }),
    }),
    [mostrar],
  )

  return (
    <Contexto.Provider value={valor}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            aria-live="polite"
            aria-atomic="false"
            className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:top-0 sm:items-end"
          >
            {avisos.map((aviso) => (
              <div
                key={aviso.id}
                className={cn(
                  'pointer-events-auto w-full max-w-sm rounded-md border border-borda border-l-4 bg-superficie p-3.5 shadow-lg',
                  TONS[aviso.tom],
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-conteudo">{aviso.titulo}</p>
                    {aviso.descricao ? (
                      <p className="mt-0.5 text-sm text-conteudo-suave">{aviso.descricao}</p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => remover(aviso.id)}
                    aria-label="Fechar aviso"
                    className="-mr-1 -mt-1 rounded p-1 text-conteudo-fraco hover:bg-superficie-3 hover:text-conteudo"
                  >
                    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
                      <path d="M6.3 5a.9.9 0 0 0-1.3 1.3L8.7 10 5 13.7A.9.9 0 0 0 6.3 15L10 11.3 13.7 15a.9.9 0 0 0 1.3-1.3L11.3 10 15 6.3A.9.9 0 0 0 13.7 5L10 8.7 6.3 5Z" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </Contexto.Provider>
  )
}

export function useAvisos(): ContextoAvisos {
  const contexto = useContext(Contexto)
  if (!contexto) throw new Error('useAvisos precisa estar dentro de <ProvedorAvisos>.')
  return contexto
}
